import {Game} from "./types";
import {
  colorEarthBrown,
  colorEarthLightBrown,
  colorGreen,
  colorLightGreen,
  colorOrange,
  colorPastelBlue,
  colorPastelOrange,
  colorPastelRed,
  colorPink,
  colorPurple,
  colorYellow
} from "../shared/colors";
import {PlaceholderFragment} from "../shared/PlaceholderFragment";
import React from "react";
import {JumbledGameFragment} from "./games/jumbled/JumbledGameFragment";
import {GameFragmentListener} from "./games/GameFragment";
import {ChessGameFragment} from "./games/chess/ChessGameFragment";
import {PairsGameFragment} from "./games/pairs/PairsGameFragment";
import {SudokuGameFragment} from "./games/sudoku/SudokuGameFragment";
import {JumbledDifficultyLevel} from "./games/jumbled/types";
import {PairsDifficultyLevel} from "./games/pairs/types";
import {SudokuDifficultyLevel} from "./games/sudoku/types";
import {SpellboundGameFragment} from "./games/spellbound/SpellboundGameFragment";
import {LetterspinDifficultyLevel} from "./games/letterspin/types";
import {LetterspinGameFragment} from "./games/letterspin/LetterspinGameFragment";
import {SlidleDifficultyLevel} from "./games/slidle/types";
import {SlidleGameFragment} from "./games/slidle/SlidleGameFragment";
import {WordmeshDifficultyLevel} from "./games/wordmesh/types";
import {WordmeshGameFragment} from "./games/wordmesh/WordmeshGameFragment";
import {DubbleupDifficultyLevel} from "./games/dubbleup/types";
import {DubbleupupGameFragment} from "./games/dubbleup/DubbleupGameFragment";
import {NonogramGameFragment} from "./games/nonogram/NonogramGameFragment";
import {SquareupDifficultyLevel} from "./games/squareup/types";
import {SquareupGameFragment} from "./games/squareup/SquareupGameFragment";

export const DAYS_HISTORY: number = 7;

export enum GameTabType {
  SUDOKU = "sudoku",
  PAIRS = "pairs",
  SPELLBOUND = "spellbound",
  JUMBLED = "jumbled",
  LETTERSPIN = "letterspin",
  SLIDLE = "slidle",
  WORDMESH = "wordmesh",
  DUBBLEUP = "dubbleup",
  NONOGRAM = "nonogram",
  SQUAREUP = "squareup",
}

export const GAME_JUMBLED: Game = {
  id: GameTabType.JUMBLED,
  name: "Jumbled",
  description: "Find words using letters on the board.",
  icon: "/games/jumbled/icon.png",
  color: colorOrange,
  levels: JumbledDifficultyLevel.VALUES.map(level => {
    return {name: level.name, text: level.text};
  }),
  render: (path, ...args) => <JumbledGameFragment path={path} gameFragmentListener={args[0] as GameFragmentListener}/>,
};

export const GAME_LETTERSPIN: Game = {
  id: GameTabType.LETTERSPIN,
  name: "Letterspin",
  description: "Find the mystery word in fewest number of guesses.",
  icon: "/games/letterspin/icon.png",
  color: colorLightGreen,
  levels: LetterspinDifficultyLevel.VALUES.map(level => {
    return {name: level.name, text: level.text};
  }),
  render: (path, ...args) => <LetterspinGameFragment path={path}
                                                     gameFragmentListener={args[0] as GameFragmentListener}/>,
};

export const GAME_SLIDLE: Game = {
  id: GameTabType.SLIDLE,
  name: "Slidle",
  description: "Slide to solve the jigsaw puzzle.",
  icon: "/games/slidle/icon.png",
  color: colorPurple,
  levels: SlidleDifficultyLevel.VALUES.map(level => {
    return {name: level.name, text: level.text};
  }),
  render: (path, ...args) => <SlidleGameFragment path={path} gameFragmentListener={args[0] as GameFragmentListener}/>,
};

export const GAME_WORDMESH: Game = {
  id: GameTabType.WORDMESH,
  name: "Wordmesh",
  description: "Connect the letters around the mesh to form words.",
  icon: "/games/wordmesh/icon.png",
  color: colorEarthLightBrown,
  levels: WordmeshDifficultyLevel.VALUES.map(level => {
    return {name: level.name, text: level.text};
  }),
  render: (path, ...args) => <WordmeshGameFragment path={path} gameFragmentListener={args[0] as GameFragmentListener}/>,
};

export const GAME_DUBBLEUP: Game = {
  id: GameTabType.DUBBLEUP,
  name: "Dubble Up",
  description: "Merge tiles with the same number to double its value.",
  icon: "/games/dubbleup/icon.png",
  color: colorEarthBrown,
  levels: DubbleupDifficultyLevel.VALUES.map(level => {
    return {name: level.name, text: level.text};
  }),
  render: (path, ...args) => <DubbleupupGameFragment path={path}
                                                     gameFragmentListener={args[0] as GameFragmentListener}/>,
};

export const GAME_PAIRS: Game = {
  id: GameTabType.PAIRS,
  name: "Pairs",
  description: "Match the pairs, improve your memory.",
  icon: "/games/pairs/icon.png",
  color: colorPastelRed,
  levels: PairsDifficultyLevel.VALUES.map(level => {
    return {name: level.name, text: level.text};
  }),
  render: (path, ...args) => <PairsGameFragment path={path} gameFragmentListener={args[0] as GameFragmentListener}/>,
};
export const GAME_SUDOKU: Game = {
  id: GameTabType.SUDOKU,
  name: "Sudoku",
  description: "An engaging numbers puzzle, even for numberphobes.",
  icon: "/games/sudoku/icon.png",
  color: colorPastelBlue,
  levels: SudokuDifficultyLevel.VALUES.map(level => {
    return {name: level.name, text: level.text};
  }),
  render: (path, ...args) => <SudokuGameFragment path={path} gameFragmentListener={args[0] as GameFragmentListener}/>,
};
export const GAME_WORDSEARCH: Game = {
  id: "wordsearch",
  name: "Wordsearch",
  description: "Search for words in a letter grid.",
  icon: "/games/sudoku/icon.png",
  color: colorPink,
  levels: [],
  render: path => <PlaceholderFragment/>,
};
export const GAME_SPELLBOUND: Game = {
  id: GameTabType.SPELLBOUND,
  name: "Spellbound",
  description: "How many words can you make?",
  icon: "/games/spellbound/icon.png",
  color: colorYellow,
  levels: [],
  render: (path, ...args) => <SpellboundGameFragment path={path}
                                                     gameFragmentListener={args[0] as GameFragmentListener}/>,
};
export const GAME_NONOGRAM: Game = {
  id: GameTabType.NONOGRAM,
  name: "Nonogram",
  description: "Paint by numbers on a grid.",
  icon: "/games/nonogram/icon.png",
  color: "#aabbcc",
  levels: [],
  render: (path, ...args) => <NonogramGameFragment path={path}
                                                   gameFragmentListener={args[0] as GameFragmentListener}/>,
};
export const GAME_SQUAREUP: Game = {
  id: GameTabType.SQUAREUP,
  name: "SquareUp",
  description: "Swap tiles on the grid to form 6 words.",
  icon: "/games/squareup/icon.png",
  color: colorPastelOrange,
  levels: SquareupDifficultyLevel.VALUES.map(level => {
    return {name: level.name, text: level.text};
  }),
  render: (path, ...args) => <SquareupGameFragment path={path} gameFragmentListener={args[0] as GameFragmentListener}/>,
};
export const GAME_CHESS: Game = {
  id: "chess",
  name: "Chess",
  description: "Play the classic strategy game.",
  icon: "/games/chess/icon.png",
  color: colorGreen,
  levels: [],
  render: (path, ...args) => <ChessGameFragment path={path} gameFragmentListener={args[0] as GameFragmentListener}/>,
};

export const GAMES: Game[] = [
  GAME_SUDOKU,
  GAME_PAIRS,
  // GAME_CHESS,
  GAME_SPELLBOUND,
  GAME_JUMBLED,
  GAME_LETTERSPIN,
  GAME_SLIDLE,
  GAME_WORDMESH,
  GAME_NONOGRAM,
  GAME_DUBBLEUP,
  GAME_SQUAREUP,
  // GAME_WORDSEARCH,
];

export function getGameById(gameId: string): Game {
  return GAMES.find(game => game.id === gameId);
}